import { createSelector } from 'reselect'
import { get, safeToJS, values } from 'utils/immutable'
import { compose } from 'ramda'

const collectionState = (state) => state.get('collection')

const selectCollectionNotification = createSelector(
  collectionState,
  get('collectionNotification')
)

export const getCollectionNotification = createSelector(
  selectCollectionNotification,
  safeToJS
)

const selectCollections = createSelector(collectionState, get('collections'))

export const getCollections = createSelector(selectCollections, safeToJS)

export const getCollectionsAsList = createSelector(
  selectCollections,
  compose(safeToJS, values)
)

const selectCollection = createSelector(collectionState, get('collection'))

export const getCollection = createSelector(selectCollection, safeToJS)

export const getIsLoading = createSelector(collectionState, get('isLoading'))

export const getHasFetchError = createSelector(
  collectionState,
  get('fetchError')
)

const selectPartUris = createSelector(selectCollection, get('partUris'))

export const getPartUris = createSelector(selectPartUris, safeToJS)

const selectCreatedCollection = createSelector(
  collectionState,
  get('createdCollection')
)

export const getCreatedCollection = createSelector(
  selectCreatedCollection,
  safeToJS
)

// Collection Lifecycle Selectors (PBI 150666)
export const getCollectionId = createSelector(selectCollection, (collection) =>
  collection ? collection.get('id') : null
)

export const getCollectionStatus = createSelector(
  selectCollection,
  (collection) => (collection ? collection.get('status') : null)
)

// Returns true only when the in-flight lifecycle operation belongs to the
// currently loaded collection. Prevents collection B's controls from being
// disabled when a stale operation for collection A is still in flight.
// Bug 505198: resetCollection now clears isStatusUpdating/statusUpdatingCollectionId,
// so stale flags from prior collections are cleaned up on navigation. The selector
// only needs to handle the case where the current collection's own request is active.
export const getIsStatusUpdating = createSelector(
  collectionState,
  selectCollection,
  (slice, collection) => {
    if (!slice.get('isStatusUpdating')) return false
    const inFlightId = slice.get('statusUpdatingCollectionId')
    const currentId = collection ? collection.get('id') : null
    // No tracked operation ID — safe to unblock (defensive; shouldn't happen)
    if (!inFlightId) return false
    // Operation in flight but collection not yet loaded — stay blocked to
    // prevent duplicate requests during the unresolved-ID window.
    if (!currentId) return true
    return String(inFlightId) === String(currentId)
  }
)

export const getIsDuplicating = createSelector(
  collectionState,
  get('isDuplicating')
)

const selectDuplicatedCollection = createSelector(
  collectionState,
  get('duplicatedCollection')
)

export const getDuplicatedCollection = createSelector(
  selectDuplicatedCollection,
  safeToJS
)

// PBI 150666: Status change notification selector
const selectStatusChangeNotification = createSelector(
  collectionState,
  get('statusChangeNotification')
)

export const getStatusChangeNotification = createSelector(
  selectStatusChangeNotification,
  safeToJS
)

// PBI 150666: Activation validation error selector
const selectActivationValidationError = createSelector(
  collectionState,
  get('activationValidationError')
)

export const getActivationValidationError = createSelector(
  selectActivationValidationError,
  safeToJS
)

export const getHasCollectionFeaturesError = createSelector(
  collectionState,
  get('collectionFeaturesError')
)

// Collection features at pinned revisions (bypasses search reducer normalization)
const selectCollectionFeatures = createSelector(
  collectionState,
  get('collectionFeatures')
)

export const getCollectionFeatures = createSelector(
  selectCollectionFeatures,
  safeToJS
)

export const getIsCollectionFeaturesLoading = createSelector(
  collectionState,
  get('isCollectionFeaturesLoading')
)
